import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { adminDisplayName, adminRoleLabel, ROLE_LABEL, SALES_LEVEL_LABEL } from "@/lib/adminDisplay";
import {
  AdminEditDrawer,
  AdminEmptyState,
  AdminField,
  AdminPageHeader,
  AdminPagination,
  paginateItems,
} from "@/pages/admin/AdminUx";

const EMPTY_CREATE = {
  email: "",
  name: "",
  title: "",
  password: "",
  password_confirm: "",
  kind: "sub_admin",
  team_id: "",
  active: true,
};

const EMPTY_RESET = { password: "", password_confirm: "" };

function kindToApi(kind) {
  if (kind === "super_admin") {
    return { role: "super_admin", department: "", sales_level: "" };
  }
  if (kind === "sales_super_admin" || kind === "team_leader" || kind === "member") {
    return { role: "sub_admin", department: "sales", sales_level: kind };
  }
  return { role: "sub_admin", department: "", sales_level: "" };
}

function kindFromUser(user) {
  if (!user) return "sub_admin";
  if (user.role === "super_admin") return "super_admin";
  if (user.department === "sales" && user.sales_level) return user.sales_level;
  return "sub_admin";
}

function fmtLogin(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 16);
  return d.toLocaleString("ko-KR", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function secretKeysIn(value) {
  if (!value || typeof value !== "object") return false;
  const banned = ["password", "password_hash", "hash", "hashed_password"];
  return Object.keys(value).some((k) => banned.includes(String(k).toLowerCase()));
}

export default function AdminUsersTab() {
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE);
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null);
  const [resetForm, setResetForm] = useState(EMPTY_RESET);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [u, t] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/sales-teams").catch(() => ({ data: [] })),
      ]);
      const rows = u.data || [];
      if (rows.some(secretKeysIn)) {
        toast.error("계정 응답에 비밀 값이 포함되어 표시를 중단합니다.");
        setUsers([]);
        return;
      }
      setUsers(rows);
      setTeams(t.data || []);
    } catch {
      toast.error("로드 실패");
    }
  };
  useEffect(() => { load(); }, []);

  const filteredUsers = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (users || []).filter((u) => {
      if (roleFilter === "sales" && u.department !== "sales") return false;
      if (roleFilter === "super_admin" && u.role !== "super_admin") return false;
      if (roleFilter === "sub_admin" && (u.role !== "sub_admin" || u.department === "sales")) return false;
      if (roleFilter === "inactive" && u.active !== false) return false;
      if (!needle) return true;
      return [u.email, u.login_id, adminDisplayName(u), u.title, u.role, u.department]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle);
    });
  }, [users, q, roleFilter]);

  const paged = paginateItems(filteredUsers, page, 12);
  useEffect(() => { setPage(1); }, [q, roleFilter]);
  useEffect(() => { if (page !== paged.page) setPage(paged.page); }, [page, paged.page]);

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!createForm.email || !createForm.password) return toast.error("아이디와 비밀번호는 필수입니다.");
    if (createForm.password !== createForm.password_confirm) return toast.error("비밀번호가 일치하지 않습니다.");
    const mapped = kindToApi(createForm.kind);
    setSaving(true);
    try {
      await api.post("/admin/users", {
        email: createForm.email.trim(),
        name: createForm.name.trim(),
        display_name: createForm.name.trim(),
        title: createForm.title.trim(),
        password: createForm.password,
        password_confirm: createForm.password_confirm,
        role: mapped.role,
        department: mapped.department || null,
        sales_level: mapped.sales_level || null,
        team_id: mapped.department === "sales" && mapped.sales_level !== "sales_super_admin" ? (createForm.team_id || null) : null,
        active: createForm.active,
      });
      toast.success("관리자 계정을 만들었습니다.");
      setCreateForm(EMPTY_CREATE);
      setCreating(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "생성 실패");
    }
    setSaving(false);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editing) return;
    const mapped = kindToApi(editing.kind);
    const isOwnerRow = editing.role === "super_admin";
    setSaving(true);
    try {
      await api.patch(`/admin/users/${editing.id}`, {
        display_name: editing.name,
        name: editing.name,
        title: editing.title,
        ...(isOwnerRow ? {} : {
          role: mapped.role,
          department: mapped.department || null,
          sales_level: mapped.sales_level || null,
          team_id: mapped.department === "sales" && mapped.sales_level !== "sales_super_admin" ? (editing.team_id || null) : null,
          active: editing.active,
        }),
      });
      toast.success("저장했습니다.");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "저장 실패");
    }
    setSaving(false);
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (!resetting) return;
    if (resetForm.password !== resetForm.password_confirm) return toast.error("비밀번호가 일치하지 않습니다.");
    setSaving(true);
    try {
      await api.post(`/admin/users/${resetting.id}/password-reset`, {
        password: resetForm.password,
        password_confirm: resetForm.password_confirm,
      });
      toast.success("비밀번호를 재설정했습니다. 해당 계정은 다시 로그인해야 합니다.");
      setResetting(null);
      setResetForm(EMPTY_RESET);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "재설정 실패");
    }
    setSaving(false);
  };

  const openEdit = (row) => {
    setEditing({
      id: row.id,
      email: row.email,
      role: row.role,
      name: adminDisplayName(row) === "관리자" && !row.display_name && !row.name ? "" : (row.display_name || row.name || ""),
      title: row.title || "",
      kind: kindFromUser(row),
      team_id: row.team_id || "",
      active: row.active !== false,
    });
  };

  return (
    <div className="space-y-4" data-testid="admin-users">
      <AdminPageHeader
        title="권한 관리"
        description="최고관리자가 관리자·영업팀 계정의 직급, 역할, 활성 상태, 비밀번호를 관리합니다."
        actions={(
          <button type="button" className="bg-white text-black rounded-lg px-3 py-2 text-sm" data-testid="users-open-create" onClick={() => setCreating(true)}>
            신규 관리자
          </button>
        )}
      />
      <div className="admin-toolbar">
        <input className="admin-toolbar__search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="아이디 / 이름 / 직급 검색" />
        <select className="admin-filter" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
          <option value="all">전체</option>
          <option value="super_admin">최고관리자</option>
          <option value="sub_admin">관리자</option>
          <option value="sales">영업팀</option>
          <option value="inactive">비활성</option>
        </select>
        <span className="admin-count">{filteredUsers.length}명</span>
      </div>

      {filteredUsers.length === 0 ? (
        <AdminEmptyState message="조건에 맞는 계정이 없습니다." />
      ) : (
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl overflow-x-auto">
          <table className="w-full text-sm min-w-[920px]">
            <thead className="text-xs uppercase tracking-wider text-white/40 border-b border-white/10">
              <tr>
                <th className="text-left py-3 px-4">아이디</th>
                <th className="text-left py-3 px-4">이름</th>
                <th className="text-left py-3 px-4">직급</th>
                <th className="text-left py-3 px-4">역할</th>
                <th className="text-left py-3 px-4">활성</th>
                <th className="text-left py-3 px-4">최근 로그인</th>
                <th className="text-left py-3 px-4">관리</th>
              </tr>
            </thead>
            <tbody>
              {paged.items.map((u) => {
                const isOwnerRow = u.role === "super_admin";
                return (
                  <tr key={u.id} className="border-b border-white/5" data-testid={`admin-user-${u.email}`}>
                    <td className="py-3 px-4">{u.email}</td>
                    <td className="py-3 px-4">{adminDisplayName(u)}</td>
                    <td className="py-3 px-4 text-white/70">{u.title || "-"}</td>
                    <td className="py-3 px-4 text-white/70">{adminRoleLabel(u)}</td>
                    <td className="py-3 px-4">
                      <span className={`admin-chip${u.active === false ? "" : " is-on"}`}>
                        {u.active === false ? "비활성" : "활성"}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-white/50">{fmtLogin(u.last_login_at)}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <button type="button" className="text-xs border border-white/15 rounded-full px-3 py-1" data-testid={`users-edit-${u.email}`} onClick={() => openEdit(u)}>수정</button>
                        <button type="button" className="text-xs border border-white/15 rounded-full px-3 py-1" data-testid={`users-reset-${u.email}`} onClick={() => { setResetting(u); setResetForm(EMPTY_RESET); }}>비밀번호 재설정</button>
                        {isOwnerRow ? <span className="text-[11px] text-white/30">보호됨</span> : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <AdminPagination page={paged.page} pageSize={12} total={filteredUsers.length} onPageChange={setPage} />

      <AdminEditDrawer
        open={creating}
        title="신규 관리자"
        testId="users-create-drawer"
        onClose={() => { setCreating(false); setCreateForm(EMPTY_CREATE); }}
        footer={(
          <>
            <button type="button" className="admin-row__btn" onClick={() => setCreating(false)}>취소</button>
            <button type="submit" form="users-create-form" disabled={saving} className="bg-white text-black rounded-lg px-4 py-2 text-sm" data-testid="users-add">
              {saving ? "저장 중…" : "계정 생성"}
            </button>
          </>
        )}
      >
        <form id="users-create-form" onSubmit={submitCreate}>
          <AdminField label="아이디 (이메일)">
            <input required autoComplete="off" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} data-testid="users-email" />
          </AdminField>
          <AdminField label="이름">
            <input value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} data-testid="users-name" />
          </AdminField>
          <AdminField label="직급">
            <input value={createForm.title} onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })} placeholder="예: 과장, 팀장" data-testid="users-title" />
          </AdminField>
          <AdminField label="역할">
            <select value={createForm.kind} onChange={(e) => setCreateForm({ ...createForm, kind: e.target.value })} data-testid="users-role">
              <option value="sub_admin">관리자</option>
              <option value="super_admin">최고관리자</option>
              <option value="sales_super_admin">{SALES_LEVEL_LABEL.sales_super_admin}</option>
              <option value="team_leader">팀장</option>
              <option value="member">영업팀</option>
            </select>
          </AdminField>
          {createForm.kind === "team_leader" || createForm.kind === "member" ? (
            <AdminField label="팀">
              <select value={createForm.team_id} onChange={(e) => setCreateForm({ ...createForm, team_id: e.target.value })}>
                <option value="">-</option>
                {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </AdminField>
          ) : null}
          <AdminField label="초기 비밀번호">
            <input type="password" required autoComplete="new-password" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} data-testid="users-password" />
          </AdminField>
          <AdminField label="비밀번호 확인">
            <input type="password" required autoComplete="new-password" value={createForm.password_confirm} onChange={(e) => setCreateForm({ ...createForm, password_confirm: e.target.value })} data-testid="users-password-confirm" />
          </AdminField>
          <AdminField label="상태">
            <select value={createForm.active ? "1" : "0"} onChange={(e) => setCreateForm({ ...createForm, active: e.target.value === "1" })}>
              <option value="1">활성</option>
              <option value="0">비활성</option>
            </select>
          </AdminField>
        </form>
      </AdminEditDrawer>

      <AdminEditDrawer
        open={!!editing}
        title="계정 수정"
        testId="users-edit-drawer"
        onClose={() => setEditing(null)}
        footer={(
          <>
            <button type="button" className="admin-row__btn" onClick={() => setEditing(null)}>취소</button>
            <button type="submit" form="users-edit-form" disabled={saving} className="bg-white text-black rounded-lg px-4 py-2 text-sm" data-testid="users-save">
              {saving ? "저장 중…" : "저장"}
            </button>
          </>
        )}
      >
        {editing ? (
          <form id="users-edit-form" onSubmit={submitEdit}>
            <AdminField label="아이디">
              <input value={editing.email} disabled />
            </AdminField>
            <AdminField label="이름">
              <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="users-edit-name" />
            </AdminField>
            <AdminField label="직급">
              <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="users-edit-title" />
            </AdminField>
            {editing.role === "super_admin" ? (
              <AdminField label="역할">
                <input value={ROLE_LABEL.super_admin} disabled />
              </AdminField>
            ) : (
              <>
                <AdminField label="역할">
                  <select value={editing.kind} onChange={(e) => setEditing({ ...editing, kind: e.target.value })} data-testid="users-edit-role">
                    <option value="sub_admin">관리자</option>
                    <option value="super_admin">최고관리자</option>
                    <option value="sales_super_admin">{SALES_LEVEL_LABEL.sales_super_admin}</option>
                    <option value="team_leader">팀장</option>
                    <option value="member">영업팀</option>
                  </select>
                </AdminField>
                {editing.kind === "team_leader" || editing.kind === "member" ? (
                  <AdminField label="팀">
                    <select value={editing.team_id} onChange={(e) => setEditing({ ...editing, team_id: e.target.value })}>
                      <option value="">-</option>
                      {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </AdminField>
                ) : null}
                <AdminField label="상태">
                  <select value={editing.active ? "1" : "0"} onChange={(e) => setEditing({ ...editing, active: e.target.value === "1" })} data-testid="users-edit-active">
                    <option value="1">활성</option>
                    <option value="0">비활성</option>
                  </select>
                </AdminField>
              </>
            )}
          </form>
        ) : null}
      </AdminEditDrawer>

      <AdminEditDrawer
        open={!!resetting}
        title="비밀번호 재설정"
        testId="users-reset-drawer"
        onClose={() => { setResetting(null); setResetForm(EMPTY_RESET); }}
        footer={(
          <>
            <button type="button" className="admin-row__btn" onClick={() => setResetting(null)}>취소</button>
            <button type="submit" form="users-reset-form" disabled={saving} className="bg-white text-black rounded-lg px-4 py-2 text-sm" data-testid="users-reset-submit">
              {saving ? "저장 중…" : "재설정"}
            </button>
          </>
        )}
      >
        {resetting ? (
          <form id="users-reset-form" onSubmit={submitReset}>
            <p className="text-xs text-white/45 mb-3">{resetting.email} 계정의 새 비밀번호를 지정합니다. 기존 비밀번호는 즉시 사용할 수 없습니다.</p>
            <AdminField label="새 비밀번호">
              <input type="password" required autoComplete="new-password" value={resetForm.password} onChange={(e) => setResetForm({ ...resetForm, password: e.target.value })} data-testid="users-reset-password" />
            </AdminField>
            <AdminField label="새 비밀번호 확인">
              <input type="password" required autoComplete="new-password" value={resetForm.password_confirm} onChange={(e) => setResetForm({ ...resetForm, password_confirm: e.target.value })} data-testid="users-reset-password-confirm" />
            </AdminField>
          </form>
        ) : null}
      </AdminEditDrawer>
    </div>
  );
}
